// The yard's tile geometry: where each owned item stands, and whether a spot is
// a place this run can actually put it.
//
// The geometry lives here rather than in `structureLayout.ts` because the
// placement rule needs it and the placement rule belongs to the spots: an item
// the keeper has put somewhere has to end up on the tile that spot names, with
// nothing else standing on it. `structureLayout` re-exports this for the
// renderer and the behaviour loop, which read the layout but never write it.
//
// Placeable equipment stands in one of the housing's fixed spots (REFOCUS §7)
// and takes that spot's world anchor; anything that lives outside the spots (the
// coop itself, the brooder, decor) keeps a stable anchor derived from its id.
// The sim and the renderer read the same table, so birds walk to the waterer the
// player sees.
//
// Moving an item is free, but the birds have to find it again: the spot it
// lands in is "relearning" for a while (BHV-19 is unresolved, so the hours are
// design values), and a moved nest box sends hens to lay on the floor.

import raw from '../data/spots.json';
import type {
  CoopFixtures,
  GameState,
  HousingType,
  SpotState,
  Structure,
} from '../core/types';
import { getConfig } from './config';
import { effectsOf, getItem, type StoreItem } from './items';
import type { Point } from '../render/iso';
import { seasonConfig, seasonPrice } from './season';

export type SpotKind = 'yard' | 'inside';

export interface SpotDef {
  id: string;
  /** i18n key. */
  name: string;
  kind: SpotKind;
  /** Setup plan position, on the 358x265 plan viewBox. */
  plan: { x: number; y: number };
  /** Grid tile the renderer anchors the item to. */
  world: { x: number; y: number };
  /** 13 values, 06:00-18:00: 0 = full sun, 1 = full shade. */
  shade: number[];
  traffic: number;
  bossVisible: boolean;
  rodentExposure: number;
  light: number;
  /** The spot opens once the flock has this many birds (0 = always). */
  unlockAtHens: number;
}

type RawSpot = Omit<SpotDef, 'kind'>;
const HOUSINGS = (raw as unknown as {
  housings: Record<HousingType, { yard: RawSpot[]; inside: RawSpot[] }>;
}).housings;

const DEFS: Record<HousingType, SpotDef[]> = {
  backyard: [
    ...HOUSINGS.backyard.yard.map((s) => ({ ...s, kind: 'yard' as const })),
    ...HOUSINGS.backyard.inside.map((s) => ({ ...s, kind: 'inside' as const })),
  ],
  balcony: [
    ...HOUSINGS.balcony.yard.map((s) => ({ ...s, kind: 'yard' as const })),
    ...HOUSINGS.balcony.inside.map((s) => ({ ...s, kind: 'inside' as const })),
  ],
};

/** Every spot a housing has, locked or not: yard first, then inside the coop. */
export function spotDefs(housing: HousingType): SpotDef[] {
  return DEFS[housing];
}

export function spotDef(housing: HousingType, id: string): SpotDef | undefined {
  return DEFS[housing].find((spot) => spot.id === id);
}

/**
 * Birds the home holds, which is what opens spots.
 *
 * A hen out past the fence still counts: spots are the run's build-out, and
 * locking one out from under a feeder the player already placed because of a
 * bad hour would be worse than the space she is not using. The per-bird share
 * of a trough is the other question, and `sim/access.ts` answers that one on the
 * birds actually in the run.
 */
function aliveBirds(state: GameState): number {
  // Counted rather than filtered: this is read by `unlockedSpots` on the
  // placement path and by every spot row the coop screen draws, and the flock
  // array was being built and thrown away each time.
  let alive = 0;
  for (const bird of state.flock) if (bird.alive) alive++;
  return alive;
}

/** Spots open right now. Backyard grows to 8 yard spots as the flock grows. */
export function unlockedSpots(state: GameState): SpotDef[] {
  const birds = aliveBirds(state);
  return spotDefs(state.housing).filter((spot) => spot.unlockAtHens <= birds);
}

export function isSpotUnlocked(state: GameState, id: string): boolean {
  return unlockedSpots(state).some((spot) => spot.id === id);
}

/**
 * The most birds a home takes, by housing and mode (REFOCUS §5, §11a). Veteran
 * is the season's call; Normal backyard is the same, and a Normal balcony is
 * the first-minute "max 3 hens" of REFOCUS §5.
 *
 * Split out from `flockCap` because the housing card is drawn before there is a
 * save to read: onboarding asks "how many hens fit" with no GameState in hand,
 * and answering that with a number from a different file is how the two drift
 * apart.,
 */
export function flockCapFor(housing: HousingType, veteran: boolean): number {
  const [, max] = seasonConfig().flockSize[housing];
  if (housing === 'balcony' && !veteran) {
    return Math.min(max, getConfig('normal').spots.balconyNormalMaxBirds);
  }
  return max;
}

/** The most birds this home takes (REFOCUS §5, §11a). */
export function flockCap(state: GameState): number {
  return flockCapFor(state.housing, state.veteran.enabled);
}

/** Where an item may stand, or null for items that live outside the spots. */
export function spotKindFor(item: StoreItem | undefined): SpotKind | null {
  if (!item) return null;
  const e = item.effects;
  if (e.nestBox || e.coopLight) return 'inside';
  if (e.providesWater || e.providesFeed || e.footbath || e.shade) return 'yard';
  return null;
}

function spotsOf(state: GameState): Record<string, SpotState> {
  return (state.spots ??= {});
}

/**
 * The spot a structure stands in, if it has one.
 *
 * `for...in` rather than `Object.entries`: this is on the hourly path for every
 * bird and every station, and materialising a two-element array per spot was
 * most of the cost of the access roll.
 */
export function spotIdOf(state: GameState, structureId: string): string | undefined {
  const spots = state.spots;
  if (!spots) return undefined;
  for (const id in spots) {
    if (spots[id].structureId === structureId) return id;
  }
  return undefined;
}

/** The spot definition a structure stands in, if it has one. */
export function spotForStructure(state: GameState, structure: Structure): SpotDef | undefined {
  const id = spotIdOf(state, structure.id);
  return id ? spotDef(state.housing, id) : undefined;
}

export function structureInSpot(state: GameState, spotId: string): Structure | undefined {
  const id = state.spots?.[spotId]?.structureId;
  return id ? state.structures.find((s) => s.id === id) : undefined;
}

/** True while birds are still finding the item that was moved into this spot. */
export function isRelearning(state: GameState, spotId: string): boolean {
  const until = state.spots?.[spotId]?.relearnUntilHour;
  return until !== undefined && state.simHour < until;
}

/** Relearning, looked up from the structure rather than the spot. */
export function isStructureRelearning(state: GameState, structure: Structure): boolean {
  const id = spotIdOf(state, structure.id);
  return id !== undefined && isRelearning(state, id);
}

// --- tile geometry ----------------------------------------------------------

/** Anchors for the items that do not stand in a spot. */
const ANCHORS: Record<string, Point> = {
  brooder_box: { x: 2, y: 2 },
  chick_waterer: { x: 1, y: 3 },
  chick_feeder: { x: 4, y: 3 },
  nesting_box: { x: 5, y: 2 },
  perch_bar: { x: 5, y: 3 },
  coop_l1: { x: 2, y: 2 },
  coop_l2: { x: 2, y: 2 },
  quarantine_pen: { x: 0, y: 0 },
  adult_waterer: { x: 1, y: 4 },
  adult_feeder: { x: 4, y: 4 },
  covered_feeder: { x: 4, y: 4 },
  auto_feeder: { x: 4, y: 4 },
  auto_waterer: { x: 1, y: 4 },
  coop_lock_timer: { x: 3, y: 4 },
  coop_light: { x: 3, y: 2 },
  footbath: { x: 3, y: 4 },
  water_barrel: { x: 0, y: 4 },
  shade_sail: { x: 4, y: 0 },
  run_netting: { x: 3, y: 0 },
  decor_flag: { x: 0, y: 0 },
  decor_planter: { x: 5, y: 0 },
};

/** Stable per-item fallback for anything the table above does not know. */
function hashedAnchor(itemId: string, width: number, height: number): Point {
  let h = 0;
  for (let i = 0; i < itemId.length; i++) h = (h * 31 + itemId.charCodeAt(i)) >>> 0;
  return { x: h % width, y: (h >> 8) % height };
}

export function footprintOf(structure: Structure): [number, number] {
  return getItem(structure.itemId)?.footprint ?? [1, 1];
}

/** Every tile a structure covers when its anchor is `spot`. */
function coveredTiles(
  structure: Structure,
  spot: Point,
  width: number,
  height: number,
): string[] {
  const [w, h] = footprintOf(structure);
  const tiles: string[] = [];
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const x = spot.x + dx;
      const y = spot.y + dy;
      if (x < 0 || y < 0 || x >= width || y >= height) continue;
      tiles.push(`${x},${y}`);
    }
  }
  return tiles;
}

/** Whether the whole footprint fits on the grid at this anchor. */
function fitsOnGrid(
  structure: Structure,
  spot: Point,
  width: number,
  height: number,
): boolean {
  const [w, h] = footprintOf(structure);
  return spot.x >= 0 && spot.y >= 0 && spot.x + w <= width && spot.y + h <= height;
}

/** Nothing is reserved, for the searches that only care about the grid. */
const NO_TILES: ReadonlySet<string> = new Set();

/**
 * Whether any tile this structure would cover is already spoken for.
 *
 * The footprint is walked rather than collected: this runs once per candidate
 * tile of the spiral search, so the array `coveredTiles` would build was the
 * larger half of the search.
 */
function occupies(
  structure: Structure,
  spot: Point,
  taken: ReadonlySet<string>,
  width: number,
  height: number,
): boolean {
  const [w, h] = footprintOf(structure);
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const x = spot.x + dx;
      const y = spot.y + dy;
      if (x < 0 || y < 0 || x >= width || y >= height) continue;
      if (taken.has(`${x},${y}`)) return true;
    }
  }
  return false;
}

/**
 * Anchor tile per structure id.
 *
 * An item the keeper put in a spot keeps that spot's world anchor: `spot.world`
 * is documented as the tile the renderer anchors the item to, and the birds walk
 * there (`sim/behavior.ts`). Spiralling a placed item away is how a nest box set
 * inside the coop ended up in the yard while the sim kept reading the coop
 * spot's shade, traffic and boss sightline for it — the spot said one thing and
 * the world drew another.
 *
 * Items with no spot — the coop, the brooder, decor — spiral to the next free
 * tile so duplicates stay individually visible. That search works on whole
 * footprints and stays on the grid: a 2x2 brooder claiming a single tile used to
 * let a waterer be placed on top of it, and a shade sail anchored at (4,0) on a
 * 4-wide balcony hung half off the run.
 */
export function layoutStructures(state: GameState): Map<string, Point> {
  const { width, height } = state.grid;
  const taken = new Set<string>();
  const layout = new Map<string, Point>();
  for (const structure of state.structures) {
    const spot = spotForStructure(state, structure);
    const base =
      spot?.world ?? ANCHORS[structure.itemId] ?? hashedAnchor(structure.itemId, width, height);
    const anchor = anchorAt(structure, base, taken, width, height, spot !== undefined);
    for (const tile of coveredTiles(structure, anchor, width, height)) taken.add(tile);
    layout.set(structure.id, anchor);
  }
  return layout;
}

/**
 * Where one structure ends up: the tile it would prefer, unless the yard says
 * otherwise.
 *
 * `placed` is why a spot's item keeps the tile its spot names. A placed item
 * only yields to a spot that cannot hold it, never to the neighbour — that
 * asymmetry is the #52 fix, so it lives in the one function both the full layout
 * and the spot-only pass go through rather than being re-derived by each.
 */
function anchorAt(
  structure: Structure,
  base: Point,
  taken: ReadonlySet<string>,
  width: number,
  height: number,
  placed: boolean,
): Point {
  if (
    fitsOnGrid(structure, base, width, height) &&
    (placed || !occupies(structure, base, taken, width, height))
  ) {
    return base;
  }
  // A yard this full has nowhere clean to put the item, but an anchor off the
  // grid is not somewhere it can be drawn or walked to at all, so the search
  // falls back to the nearest tile whose whole footprint is on the run.
  return (
    firstFreeTile(structure, base, taken, width, height) ??
    firstInBoundsTile(structure, base, width, height) ??
    base
  );
}

/**
 * The nearest tile to `centre` where this structure fits clear of `taken`.
 */
function firstFreeTile(
  structure: Structure,
  centre: Point,
  taken: ReadonlySet<string>,
  width: number,
  height: number,
): Point | undefined {
  return firstAnchor(structure, centre, taken, width, height, true);
}

/**
 * The nearest tile to `centre` where this structure's whole footprint fits.
 */
function firstInBoundsTile(
  structure: Structure,
  centre: Point,
  width: number,
  height: number,
): Point | undefined {
  return firstAnchor(structure, centre, NO_TILES, width, height, false);
}

/**
 * The square rings around `centre`, nearest first, and the first tile on them
 * this structure can take.
 *
 * One walk with no per-ring array. The shape it replaced built a fresh array of
 * every point on every ring and handed it to `find`, which on a 6x6 run is 455
 * point objects for a sweep that finds nothing — paid per spotless structure,
 * per layout, which on a full save came to about 60,000 of them a purchase.
 *
 * `needsClear` is the whole difference between the two things callers ask for:
 * gear hunting a clear tile, and the fallback that only needs somewhere its
 * footprint is on the run. Same rings, same order, same first hit.
 */
function firstAnchor(
  structure: Structure,
  centre: Point,
  taken: ReadonlySet<string>,
  width: number,
  height: number,
  needsClear: boolean,
): Point | undefined {
  const [w, h] = footprintOf(structure);
  // A clear anchor needs w*h free tiles. A yard holding fewer has none, however
  // far the spiral walks, so the count answers first and the rings are never
  // walked. This is the full-yard case, and it is the one a player actually
  // reaches, because a backyard has 11 spots and the save grows past that.
  if (needsClear && width * height - taken.size < w * h) return undefined;
  for (let radius = 0; radius <= Math.max(width, height); radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
        const x = centre.x + dx;
        const y = centre.y + dy;
        if (x < 0 || y < 0 || x >= width || y >= height) continue;
        // `fitsOnGrid`, given the bounds check above.
        if (x + w > width || y + h > height) continue;
        if (needsClear && occupies(structure, { x, y }, taken, width, height)) continue;
        return { x, y };
      }
    }
  }
  return undefined;
}

/**
 * The structure whose footprint covers a tile, if any. A tile two structures
 * cover belongs to the one anchored on it: the keeper put that one there on
 * purpose, and the other is only passing over the corner.
 *
 * Anchored-on-the-tile is not the last word, though. The coop's 2x2 is anchored
 * at (2,2) and the backyard's `in1` — the dark back corner the nest box goes in
 * — is anchored at (2,2) too, so both are *on* the tile and the first one the
 * list happened to hold answered the tap. That is the coop, which the keeper
 * never chose to put there. Gear in a spot is what was placed on purpose
 * (`spotIsFreeFor` lets buildings share tiles precisely because the gear is the
 * thing that matters), so a placed item outranks a building standing on the same
 * anchor.
 */
export function structureAtTile(
  state: GameState,
  layout: Map<string, Point>,
  x: number,
  y: number,
): Structure | undefined {
  const covering = state.structures.filter((s) => coversTile(s, layout.get(s.id), x, y));
  const anchored = covering.filter(
    (s) => layout.get(s.id)?.x === x && layout.get(s.id)?.y === y,
  );
  return anchored.find((s) => spotIdOf(state, s.id) !== undefined) ?? anchored[0] ?? covering[0];
}

function coversTile(structure: Structure, spot: Point | undefined, x: number, y: number): boolean {
  if (!spot) return false;
  const [w, h] = footprintOf(structure);
  return x >= spot.x && x < spot.x + w && y >= spot.y && y < spot.y + h;
}

/**
 * Whether `structure` could stand on this spot's tile without sharing it with
 * another piece of gear.
 *
 * This is the question the placement rule asks before it lets a drop through.
 * The structure itself never counts against its own footprint, so a keeper
 * re-placing an item where it already stands, or moving it out of a spot that
 * shares a tile, gets the same answer as a first-time drop.
 *
 * Only gear blocks a spot. Buildings — the coop, the brooder, decor — are the
 * run's fixed furniture and are not exclusive: `y6` is anchored at (3,2), which
 * is inside the coop's 2x2 and is shared with the inside spot `in2` on purpose
 * (`sim/behavior.ts` reads the yard spot there in preference to the one behind
 * it). Two pieces of gear on one tile is different, and that is the case that
 * has to be refused: a 2x2 shade sail dropped in `y7` reached across into `y3`
 * and drew on top of the feeder the keeper had already put there, and the tap
 * that tile answered with the sail.
 */
export function spotIsFreeFor(state: GameState, spotId: string, structure: Structure): boolean {
  const { width, height } = state.grid;
  return spotIsFreeForPlaced(
    placedAnchors(state),
    spotDef(state.housing, spotId),
    structure,
    width,
    height,
  );
}

/**
 * The rule, against a set of placed anchors the caller already holds.
 *
 * Split out so the one caller that asks the question in a loop — `ensureSpots`,
 * trying each open spot in turn — can lay the yard out once for the whole sweep
 * instead of once per candidate. There is no cache and no key to get wrong: the
 * caller owns the value and rebuilds it in the one place its own code changes
 * the answer.
 */
function spotIsFreeForPlaced(
  placed: PlacedAnchor[],
  def: SpotDef | undefined,
  structure: Structure,
  width: number,
  height: number,
): boolean {
  if (!def) return false;
  if (!fitsOnGrid(structure, def.world, width, height)) return false;
  const wanted = new Set(coveredTiles(structure, def.world, width, height));
  if (wanted.size === 0) return false;
  for (const { structure: other, anchor } of placed) {
    if (other.id === structure.id) continue;
    for (const tile of coveredTiles(other, anchor, width, height)) {
      if (wanted.has(tile)) return false;
    }
  }
  return true;
}

interface PlacedAnchor {
  structure: Structure;
  anchor: Point;
}

/**
 * Where each *placed* structure stands, and nothing else.
 *
 * This is the question `spotIsFreeFor` actually asks — it weighs the footprints
 * of gear that is in a spot, because buildings are not exclusive — and a placed
 * structure's anchor depends on nothing but its own spot. `anchorAt` consults
 * the occupied tile set for spotless structures only, so a full yard's worth of
 * them cannot change this. That holds on the fallback too: when a spot's tile
 * cannot hold the item, searching with nothing reserved finds the nearest
 * in-bounds tile, which is what the original reached only after the reserved
 * search came back with nothing. Laying out the spotless ones was the entire
 * cost — `ensureSpots` asks the rule once per candidate spot and the coop
 * screen once per spot in the table, and each question was rebuilding the whole
 * yard from scratch to read eleven tiles.
 *
 * Building this from the shared `anchorAt` is what keeps it honest. A second
 * copy of the placement rule here would be free to drift from `layoutStructures`
 * and put the sim's answer back on a different tile than the world's.
 */
function placedAnchors(state: GameState): PlacedAnchor[] {
  const { width, height } = state.grid;
  const placed: PlacedAnchor[] = [];
  for (const structure of state.structures) {
    const spot = spotForStructure(state, structure);
    if (spot === undefined) continue;
    placed.push({
      structure,
      anchor: anchorAt(structure, spot.world, NO_TILES, width, height, true),
    });
  }
  return placed;
}

/**
 * Brings the spot table in line with the structures that exist: forgets
 * structures that are gone and puts every placeable item that has no spot into
 * the first free one of its kind. Deterministic (structure order, spot order),
 * so a replay places things where the live game did. Saves from before spots
 * existed get their equipment placed on first load this way.
 */
export function ensureSpots(state: GameState): void {
  const spots = spotsOf(state);
  const present = new Set(state.structures.map((s) => s.id));
  for (const spot of Object.values(spots)) {
    if (spot.structureId && !present.has(spot.structureId)) delete spot.structureId;
  }
  const placed = new Set(
    Object.values(spots)
      .map((spot) => spot.structureId)
      .filter((id): id is string => Boolean(id)),
  );
  // The open-spot list is read once for the whole sweep: it answers to the
  // housing and the flock, and this loop writes neither. Rebuilding it per
  // structure was a per-structure filter of the table on a save that carries
  // hundreds of spotless ones.
  const open = unlockedSpots(state);
  // The yard laid out, held across the sweep because the rule asks the same
  // question once per candidate spot. It is rebuilt in the one line below that
  // changes the answer, which is the only way this loop can change it — so
  // there is no cache to go stale, only a value its owner keeps current.
  let anchors = placedAnchors(state);
  for (const structure of state.structures) {
    if (placed.has(structure.id)) continue;
    const kind = spotKindFor(getItem(structure.itemId));
    if (!kind) continue;
    const free = open.find(
      (spot) =>
        spot.kind === kind &&
        !spots[spot.id]?.structureId &&
        spotIsFreeForPlaced(anchors, spot, structure, state.grid.width, state.grid.height),
    );
    if (!free) continue;
    spots[free.id] = { ...(spots[free.id] ?? {}), structureId: structure.id };
    placed.add(structure.id);
    anchors = placedAnchors(state);
  }
}

export type PlaceReason =
  | 'no_such_spot'
  | 'locked'
  | 'occupied'
  | 'wrong_kind'
  | 'no_such_item'
  | 'blocked';

/**
 * The refusals a spot gives about itself. `no_such_spot` is not one: that is the
 * housing saying there is no such place at all, which has no spot to hint on.
 */
export type SpotRefusal = Exclude<PlaceReason, 'no_such_item' | 'no_such_spot'>;

export type PlaceResult = { ok: true } | { ok: false; reason: PlaceReason };

/**
 * How one spot answers a drop, and everything a player needs to be told why.
 *
 * The refusal alone was never enough. `locked` with no bird count is the same
 * word at one bird and at five, so a keeper tapping a spot that will not open
 * learns nothing about waiting for it — which is the gap `items.ts` already
 * closed for the store with `GATE_HINTS` ("a bare 'locked' with no reason was
 * a teen quit trigger"). This is the same answer for the other half of the
 * loadout, so the numbers a hint needs travel with the refusal rather than
 * being re-derived by whichever screen ends up drawing the row.
 *
 * `checkTarget` below reads this rather than repeating the ladder, so what a
 * drop is refused for and what the spot says about itself cannot drift apart.
 */
export interface SpotAvailability {
  spotId: string;
  /** i18n key for the spot's name, out of the spot table. */
  nameKey: string;
  /** Where gear stands: the yard, or inside the coop. */
  kind: SpotKind;
  /** 'open', or the refusal a drop into this spot gets. */
  state: 'open' | SpotRefusal;
  /** Live birds, which is what the locked hint counts against. */
  birds: number;
  /** The flock this spot opens at; 0 means it never closes. */
  unlockAtHens: number;
  /** The item standing here, when one is. */
  occupantItemId?: string;
  /** The kind the dropped item wants, when the spot is the wrong one. */
  wantsKind?: SpotKind;
}

/**
 * What this spot will say about itself, for an item that may or may not be
 * dropped on it. Null when the housing has no such spot.
 *
 * The order is the order a keeper asks in: is it a spot, is it open yet, is it
 * the right side of the coop, is anything in it, does the tile take it.
 */
export function spotAvailability(
  state: GameState,
  spotId: string,
  structure?: Structure,
): SpotAvailability | null {
  const def = spotDef(state.housing, spotId);
  if (!def) return null;
  const base: SpotAvailability = {
    spotId,
    nameKey: def.name,
    kind: def.kind,
    state: 'open',
    birds: aliveBirds(state),
    unlockAtHens: def.unlockAtHens,
  };
  if (!isSpotUnlocked(state, spotId)) return { ...base, state: 'locked' };
  // Kind before occupancy: a yard spot is the wrong side of the coop whatever is
  // standing on it, and "this is the wrong kind" is the thing the keeper can act
  // on. That is also the order the refusal contract had, so the two cannot
  // disagree about which word a drop gets.
  if (structure && spotKindFor(getItem(structure.itemId)) !== def.kind) {
    const wants = spotKindFor(getItem(structure.itemId));
    return { ...base, state: 'wrong_kind', wantsKind: wants ?? undefined };
  }
  const occupantId = state.spots?.[spotId]?.structureId;
  const occupant = occupantId ? state.structures.find((s) => s.id === occupantId) : undefined;
  if (occupant && occupant.id !== structure?.id) {
    return { ...base, state: 'occupied', occupantItemId: occupant.itemId };
  }
  // Free, open, the right kind and empty, but the tile will not hold the whole
  // item. A 2x2 shade sail dropped where a feeder already stands used to be
  // accepted here, then drew on top of it and answered the keeper's tap, so the
  // placement the sim cannot honour is refused rather than left to look broken.
  if (structure && !spotIsFreeFor(state, spotId, structure)) {
    return { ...base, state: 'blocked' };
  }
  return base;
}

/** Which side of the coop a kind is, in the words the hints use. */
const KIND_WORDS: Record<SpotKind, { en: string; es: string }> = {
  yard: { en: 'yard', es: 'patio' },
  inside: { en: 'inside the coop', es: 'dentro del gallinero' },
};

/**
 * Why a spot is not open, in a sentence. Same shape as `GATE_HINTS` in
 * `items.ts`: both languages in the sim layer, because both the row that draws
 * it and the gate that refuses a purchase are sim code and must say the same
 * thing. `{n}`, `{birds}`, `{item}` and `{kind}` are filled from the
 * availability row, so a hint cannot quote a number the spot does not hold.
 */
export const SPOT_HINTS: Record<SpotRefusal, { en: string; es: string }> = {
  locked: {
    en: 'Locked — opens at {n} birds. You have {birds}.',
    es: 'Bloqueado — se abre con {n} aves. Tienes {birds}.',
  },
  occupied: {
    en: 'Something is already here: {item}.',
    es: 'Ya hay algo aquí: {item}.',
  },
  wrong_kind: {
    en: 'This is a {kind} spot. That goes {kindWanted}.',
    es: 'Este es un lugar del {kind}. Eso va {kindWanted}.',
  },
  blocked: {
    en: 'Not enough clear tile here for something this size.',
    es: 'No hay baldosa libre aquí para algo de ese tamaño.',
  },
};

/** The filled-in hint for a spot that is not open, or null when it is. */
export function spotRefusalText(
  availability: SpotAvailability,
  locale: 'en' | 'es' = 'en',
): string | null {
  if (availability.state === 'open') return null;
  const item = availability.occupantItemId
    ? getItem(availability.occupantItemId)?.name[locale]
    : undefined;
  const values: Record<string, string | number> = {
    n: availability.unlockAtHens,
    birds: availability.birds,
    kind: KIND_WORDS[availability.kind][locale],
    kindWanted: availability.wantsKind
      ? KIND_WORDS[availability.wantsKind][locale]
      : KIND_WORDS[availability.kind === 'yard' ? 'inside' : 'yard'][locale],
  };
  if (item) values.item = item;
  return SPOT_HINTS[availability.state][locale].replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

function checkTarget(state: GameState, spotId: string, structure: Structure): PlaceResult {
  const availability = spotAvailability(state, spotId, structure);
  if (!availability) return { ok: false, reason: 'no_such_spot' };
  return availability.state === 'open'
    ? { ok: true }
    : { ok: false, reason: availability.state };
}
/** Hours until birds use an item moved into a new spot (design, BHV-19). */
function relearnHoursFor(structure: Structure): number {
  const s = getConfig('normal').spots;
  return effectsOf(structure).nestBox ? s.nestRelearnHours : s.relearnHours;
}

/**
 * Puts an owned item into a spot. An item that already stands somewhere is
 * moved, which is free but costs the birds a relearning period.
 */
export function placeItem(state: GameState, spotId: string, structureId: string): PlaceResult {
  const structure = state.structures.find((s) => s.id === structureId);
  if (!structure) return { ok: false, reason: 'no_such_item' };
  const from = spotIdOf(state, structureId);
  if (from === spotId) return { ok: true };
  if (from) return moveItem(state, from, spotId);
  const check = checkTarget(state, spotId, structure);
  if (!check.ok) return check;
  const spots = spotsOf(state);
  spots[spotId] = { ...(spots[spotId] ?? {}), structureId };
  return { ok: true };
}

/** Moves whatever stands in `from` to the empty spot `to`. */
export function moveItem(state: GameState, from: string, to: string): PlaceResult {
  const structure = structureInSpot(state, from);
  if (!structure) return { ok: false, reason: 'no_such_item' };
  if (from === to) return { ok: true };
  const check = checkTarget(state, to, structure);
  if (!check.ok) return check;
  const spots = spotsOf(state);
  delete spots[from].structureId;
  delete spots[from].relearnUntilHour;
  spots[to] = { structureId: structure.id, relearnUntilHour: state.simHour + relearnHoursFor(structure) };
  return { ok: true };
}

// --- fixtures ----------------------------------------------------------------

export type RoofType = CoopFixtures['roof'];
export type Ventilation = CoopFixtures['ventilation'];

/** The coop as it comes: bare tin, no vent, no roost bar (REFOCUS §7, §11b). */
export function fixturesOf(state: GameState): CoopFixtures {
  return state.fixtures ?? { roof: 'bare_tin', ventilation: 'none', roost: false };
}

export type FixtureChoice =
  | { slot: 'roof'; value: RoofType }
  | { slot: 'ventilation'; value: Ventilation }
  | { slot: 'roost'; value: boolean };

/** The catalog item a fixture choice is priced as, or null when it is free. */
export function fixtureItemId(choice: FixtureChoice): string | null {
  switch (choice.slot) {
    case 'roof':
      return choice.value === 'bare_tin' ? null : `roof_${choice.value}`;
    case 'ventilation':
      return choice.value === 'none' ? null : `vent_${choice.value}`;
    case 'roost':
      return choice.value ? 'perch_bar' : null;
  }
}

export type FixtureResult = { ok: true; cost: number } | { ok: false; reason: 'too_poor' };

/**
 * Changes a coop fixture, paying the season's price for the new one. Going back
 * to what the coop came with is free; nothing is refunded.
 */
export function setFixture(state: GameState, choice: FixtureChoice): FixtureResult {
  const current = fixturesOf(state);
  if (current[choice.slot] === choice.value) return { ok: true, cost: 0 };
  const itemId = fixtureItemId(choice);
  const cost = itemId ? seasonPrice(itemId, getItem(itemId)?.price ?? 0) : 0;
  if (state.eggits < cost) return { ok: false, reason: 'too_poor' };
  state.eggits -= cost;
  state.fixtures = { ...current, [choice.slot]: choice.value } as CoopFixtures;
  return { ok: true, cost };
}
